<template>
  <ValidationProvider
    v-slot="{ errors, valid }"
    :vid="(group != '' ? group + '_' : '') + vid"
    :name="($attrs.name as string) || ($attrs.label as string)"
    :rules="rulesWithoutRequiredRulesIfValidationDisabled"
    :model-value="innerValue"
    @update:model-value="updateValue"
  >
    <b-field
      :type="{
        'is-normal': disabled,
        'is-danger': showValidationErrors && (errors[0] || customError.length > 0),
        'is-success': showValidationErrors && valid,
      }"
      :message="showValidationErrors ? [errors + customError] : ''"
      class="InputWithValidation-field"
    >
      <template #label>
        <FormInputLabel
          :validation-activated="validationActivated"
          :show-validation-errors="showValidationErrors"
          :label="($attrs.label as string) || ''"
          :is-mandatory="isMandatory"
          :disabled="disabled"
          :input-notice="inputNotice"
          :tooltip="tooltip"
        />
      </template>

      <div :class="`InputWithValidation-control-container ${controlRight ? 'control-right' : ''}`">
        <p v-if="control" class="InputWithValidation-control">
          <span class="button is-static">{{ control }}</span>
        </p>
        <p v-if="controlAction" class="InputWithValidation-control">
          <slot></slot>
        </p>
        <VueQuillCustom :content="innerValue" @change="testDisplay($event)" />
      </div>
    </b-field>
  </ValidationProvider>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import FormInputLabel from "./FormInputLabel.vue"
import VueQuillCustom from "./VueQuillCustom.vue"
import { BField } from "buefy"

export default defineComponent({
  name: "RichInputTextWithValidation",
  components: {
    ValidationProvider,
    FormInputLabel,
    VueQuillCustom,
    BField,
  },
  props: {
    group: {
      type: String,
      default: "",
      required: false,
    },
    vid: {
      type: String,
      required: false,
      default: "",
    },
    rules: {
      type: [String, Array],
      required: false,
      default: "",
    },
    modelValue: {
      type: String,
      required: false,
      default: "",
    },
    tooltip: {
      type: String,
      required: false,
      default: "",
    },
    inputNotice: {
      type: String,
      required: false,
      default: "",
    },
    control: {
      type: String,
      required: false,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
      required: false,
    },
    customError: {
      type: String,
      default: "",
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    passwordReveal: {
      type: Boolean,
      default: false,
      required: false,
    },
    maxlength: {
      type: Number,
      default: 0,
      required: false,
    },
    controlRight: {
      type: Boolean,
      default: false,
      required: false,
    },
    controlAction: {
      type: Boolean,
      default: false,
      required: false,
    },
    autocomplete: {
      type: String,
      default: "",
      required: false,
    },
    validationActivated: {
      type: Boolean,
      default: true,
      required: false,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
      required: false,
    },
    inCard: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  emits: ["update:modelValue", "manual-change"],
  data() {
    return {
      innerValue: "",
      editorOption: {
        theme: "snow",
      },
      isMandatory: false,
      rulesWithoutRequiredRulesIfValidationDisabled: "",
    }
  },
  watch: {
    innerValue(newVal: string, _oldValue: string): void {
      this.$emit("update:modelValue", newVal)
    },
    modelValue(newVal: string, _oldValue: string): void {
      this.innerValue = newVal
      if (newVal != _oldValue) {
        this.$emit("manual-change", newVal)
      }
    },
    rules(): void {
      this.onRulesChanged()
    },
    validationActivated(): void {
      this.onRulesChanged()
    },
  },
  created(): void {
    if (this.modelValue != null) {
      this.innerValue = this.modelValue
    }
    this.onRulesChanged()
  },
  mounted() {
    this.$nextTick(() => {
      this.adjustTextAreaHeight()
    })
  },
  methods: {
    adjustTextAreaHeight() {
      // Access the input element using the ref
      const field = this.$refs.inputField as any

      // Check if the input element is available and correctly referenced
      if (!field || !field.$el) {
        console.error("Input field not found.")
        return
      }
      // Assuming the actual input element is the root element of 'b-input' or within it
      const actualInput = field.$el.querySelector("textarea")

      if (!actualInput || !actualInput.style) {
        console.error("Actual input field not found.")
        return
      }

      // Reset field height
      actualInput.style.height = "inherit"

      // Calculate the height
      const computed = window.getComputedStyle(actualInput)
      const height =
        parseInt(computed.getPropertyValue("border-top-width"), 10) +
        parseInt(computed.getPropertyValue("padding-top"), 10) +
        actualInput.scrollHeight +
        parseInt(computed.getPropertyValue("padding-bottom"), 10) +
        parseInt(computed.getPropertyValue("border-bottom-width"), 10)

      actualInput.style.height = height + "px"
    },

    testDisplay(event: any) {
      this.innerValue = event.html
    },

    onRulesChanged(): void {
      // Handle both string and array formats for rules
      if (this.rules) {
        if (Array.isArray(this.rules)) {
          this.isMandatory = this.rules.includes("required")
        } else {
          // String format like "required|email"
          this.isMandatory = this.rules.includes("required")
        }
      } else {
        this.isMandatory = false
      }

      if (this.rules) {
        this.rulesWithoutRequiredRulesIfValidationDisabled = this.rules.toString()
        if (!this.validationActivated) {
          this.rulesWithoutRequiredRulesIfValidationDisabled = this.rules
            .toString()
            .replace("required|", "")
            .replace("|required", "")
            .replace("required", "")
        }
      } else {
        this.rulesWithoutRequiredRulesIfValidationDisabled = ""
      }
    },

    updateValue(newValue: string): void {
      this.innerValue = newValue
      // Émettre immédiatement pour synchroniser avec le parent
      this.$emit("update:modelValue", newValue)
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.InputWithValidation-field {
  margin-bottom: $input-field-margin-bot;

  .InputWithValidation-control {
    margin-left: 10px;

    span {
      width: 100%;
    }

    .is-static {
      color: $dark;
    }
  }
}

.InputWithValidation-control-container {
  display: flex;

  &.control-right {
    flex-direction: row-reverse;
  }

  .control {
    width: 100%;
  }
}

.auto-expand {
  min-height: 30px; // Minimum height
  overflow-y: hidden; // Hide vertical scrollbar
  resize: none; // Disable manual resize
}

.InputWithValidation-fieldLabel {
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  width: 100%;

  .mandatory {
    padding-left: 20px;
    color: $dark;
  }
}

#app .InputWithValidation-disabled {
  color: $text-default-color;
}

.tox {
  width: 100% !important;
}

ul {
  margin: 0;
  padding: 0;
  list-style-type: none;
  line-height: 1.5;
}

li {
  margin: 0 0 10px 0;
  padding: 0;
}
</style>
